import React, { useEffect, useRef, useState } from 'react';
import { Stack, SxProps } from '@mui/material';
import { Checkbox, Fields, Form, FormRef, StepSlider } from 'common/form';
import { ConfirmationModalType } from 'services/modals/constants';
import { useConfirmationModal } from 'services/modals/hooks';
import Yup from 'utils/validation';
import {
  useGenerateAdjustedAnalysisRequirementMutation,
  useGenerateAnalysisRequirementRecommendationMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { RequirementItemView } from './RequirementItemView';
import { RequirementRecommendationView } from './RequirementRecommendationView';
import { RequirementRecommendationCategory } from 'generated-graphql/types';

const marks = [
  {
    label: 'Unimportant',
    value: 0
  },
  {
    value: 0.5
  },
  {
    label: 'Neutral',
    value: 1
  },
  {
    value: 1.5
  },
  {
    label: 'Very important',
    value: 2
  }
];

const sliderContainer: SxProps = {
  display: 'flex',
  alignItems: 'center',
  mx: 1
};

const schema = Yup.object().shape({
  content: Yup.string().trim().required('Enter requirement.')
});

type RequirementSettingItemFormData = Yup.InferType<typeof schema>;

const fields: Fields<RequirementSettingItemFormData> = {
  content: {
    type: 'textarea',
    autoFocus: true,
    isMulti: true,
    rows: 3
  }
};

type Props = {
  index: number;
  content: string;
  weight: number;
  isRecommendationDismissed?: boolean | null;
  recommendationCategory?: RequirementRecommendationCategory | null;
  recommendationText?: string | null;
  setFieldValue: (field: string, value: any) => void;
  setFieldError: (field: string, value: string | undefined) => void;
  deleteRequirement?: () => void;
  fieldName: string;
  isMandatory: boolean;
  error?: boolean;
  candidateBatchId?: string;
  candidateBatchCandidatesCount?: number | null;
  requirementMetCandidatesCount?: number | null;
  onAddMultipleRequirements: (index: number, requirements: string[]) => void;
  onUndoMultipleRequirementsAdjustment: (
    index: number,
    originalContent: string,
    requirements: string[]
  ) => void;
};

export const RequirementSettingItem: React.FC<Props> = ({
  index,
  content,
  weight,
  setFieldValue,
  setFieldError,
  deleteRequirement,
  fieldName,
  isMandatory,
  isRecommendationDismissed,
  recommendationCategory,
  recommendationText,
  onAddMultipleRequirements,
  onUndoMultipleRequirementsAdjustment,
  error,
  candidateBatchId,
  candidateBatchCandidatesCount,
  requirementMetCandidatesCount
}) => {
  const [isEditing, setIsEditing] = useState(content === '');
  const [originalValues, setOriginalValues] = useState<
    RequirementSettingItemFormData & { isMandatory: boolean; weight: number }
  >({
    content,
    isMandatory,
    weight
  });

  const [originalRequirementContent, setOriginalRequirementContent] = useState<
    string | undefined
  >(undefined);
  const [adjustedMultipleRequirements, setAdjustedMultipleRequirements] =
    useState<string[]>([]);
  const [
    adjustedRequirementExplanationText,
    setAdjustedRequirementExplanationText
  ] = useState<string | undefined>(undefined);
  const [isRecommendationCompliant, setIsRecommendationCompliant] =
    useState(false);

  const { showModal } = useConfirmationModal();

  const contentField = `${fieldName}[${index}].content`;
  const mandatoryField = `${fieldName}[${index}].isMandatory`;
  const weightField = `${fieldName}[${index}].weight`;
  const recommendationDismissedField = `${fieldName}[${index}].isRecommendationDismissed`;
  const recommendationCategoryField = `${fieldName}[${index}].recommendationCategory`;
  const recommendationTextField = `${fieldName}[${index}].recommendationText`;

  const [
    generateAnalysisRequirementRecommendation,
    { loading: recommendationLoading }
  ] = useGenerateAnalysisRequirementRecommendationMutation();

  const [
    generateAdjustedAnalysisRequirement,
    { loading: adjustedRequirementLoading }
  ] = useGenerateAdjustedAnalysisRequirementMutation();

  const formRef = useRef<FormRef<RequirementSettingItemFormData>>(null);

  const handleDelete = () => {
    showModal({
      title: 'Delete requirement?',
      message: 'This cannot be undone',
      onDestructiveConfirm: () => {
        deleteRequirement?.();
        setFieldError(contentField, undefined);
      },
      modalType: ConfirmationModalType.DESTRUCTIVE,
      cancelText: 'Cancel',
      destructiveText: 'Delete'
    });
  };

  const onHandleCancel = () => {
    if (!originalValues.content) {
      handleDelete();
      return;
    }

    setFieldValue(contentField, originalValues.content);
    setFieldValue(mandatoryField, originalValues.isMandatory);
    setFieldValue(weightField, originalValues.weight);

    setIsEditing(false);
    setFieldError(contentField, undefined);
  };

  const resetRecommendations = () => {
    setAdjustedMultipleRequirements([]);
    setAdjustedRequirementExplanationText(undefined);
    setOriginalRequirementContent(undefined);
  };

  const generateRecommendations = async (content: string) => {
    resetRecommendations();
    const { data: recommendationData } =
      await generateAnalysisRequirementRecommendation({
        variables: {
          input: {
            analysisRequirementText: content
          }
        }
      });

    if (!recommendationData?.generateAnalysisRequirementRecommendation) {
      return;
    }

    setIsRecommendationCompliant(
      recommendationData.generateAnalysisRequirementRecommendation.isCompliant
    );
    if (
      !recommendationData?.generateAnalysisRequirementRecommendation.isCompliant
    ) {
      setFieldValue(recommendationDismissedField, true);
    }
    setFieldValue(recommendationDismissedField, false);
    setFieldValue(
      recommendationCategoryField,
      recommendationData?.generateAnalysisRequirementRecommendation
        .recommendationCategory
    );
    setFieldValue(
      recommendationTextField,
      recommendationData?.generateAnalysisRequirementRecommendation
        .recommendationText
    );
  };

  const onHandleSubmit = async (data: RequirementSettingItemFormData) => {
    setFieldValue(contentField, data.content);

    // Update original values to the new saved values
    setOriginalValues({ ...data, isMandatory, weight });

    setIsEditing(false);
    setFieldError(contentField, undefined);

    await generateRecommendations(data.content);
  };

  // Auto-save functionality using the existing handleChange pattern
  const handleAutoSave = (formRef: FormRef<RequirementSettingItemFormData>) => {
    return formRef.watch(
      (value: RequirementSettingItemFormData, { name }: { name: string }) => {
        if (name === 'content') {
          setFieldValue(contentField, value.content);
        }
      }
    ).unsubscribe;
  };

  useEffect(() => {
    if (!formRef?.current || !isEditing) {
      return;
    }

    const subscription = handleAutoSave(formRef.current);

    return () => subscription();
  }, [
    formRef,
    isEditing,
    contentField,
    mandatoryField,
    weightField,
    setFieldValue
  ]);

  const handleEdit = () => {
    setOriginalValues({
      content,
      isMandatory,
      weight
    });
    setIsEditing(true);
  };

  const handleDismissRecommendation = () => {
    setFieldValue(recommendationDismissedField, true);
    if (isRecommendationCompliant) {
      setIsRecommendationCompliant(false);
    }
  };

  const handleFixRecommendation = async (
    recommendationText: string,
    recommendationCategory: RequirementRecommendationCategory
  ) => {
    const { data } = await generateAdjustedAnalysisRequirement({
      variables: {
        input: {
          analysisRequirementText: content,
          recommendationCategory,
          recommendationText,
          candidateBatchId
        }
      }
    });

    if (!data?.generateAdjustedAnalysisRequirement) {
      return;
    }

    setOriginalRequirementContent(content);
    setAdjustedRequirementExplanationText(
      data.generateAdjustedAnalysisRequirement.adjustmentExplanationText
    );

    if (
      recommendationCategory !==
      RequirementRecommendationCategory.MultipleSkills
    ) {
      setFieldValue(
        contentField,
        data.generateAdjustedAnalysisRequirement
          .adjustedAnalysisRequirementText[0]
      );
    } else {
      setAdjustedMultipleRequirements(
        data.generateAdjustedAnalysisRequirement.adjustedAnalysisRequirementText
      );
      onAddMultipleRequirements(
        index,
        data.generateAdjustedAnalysisRequirement.adjustedAnalysisRequirementText
      );
    }
  };

  const handleUndo = () => {
    if (
      recommendationCategory ===
        RequirementRecommendationCategory.MultipleSkills &&
      originalRequirementContent
    ) {
      onUndoMultipleRequirementsAdjustment(
        index,
        originalRequirementContent,
        adjustedMultipleRequirements
      );
      setAdjustedRequirementExplanationText(undefined);
      setOriginalRequirementContent(undefined);
      setAdjustedMultipleRequirements(adjustedMultipleRequirements);
      return;
    }

    setFieldValue(contentField, originalRequirementContent);
    setAdjustedRequirementExplanationText(undefined);
    setOriginalRequirementContent(undefined);
  };

  useEffect(() => {
    if (error) {
      formRef.current?.submit();
    }
  }, [error]);

  return (
    <Stack gap="15px">
      {isEditing ? (
        <Stack direction="column" gap="20px" mt="15px">
          <Form
            ref={formRef}
            schema={schema}
            fields={fields}
            sx={{ gap: '20px' }}
            submitButtonProps={{
              label: 'Save requirement',
              variant: 'primary'
            }}
            onCancel={onHandleCancel}
            onSubmit={onHandleSubmit}
            defaultValues={{
              content,
              isMandatory,
              weight
            }}
          />
        </Stack>
      ) : (
        <Stack direction="column" gap="15px">
          <RequirementItemView
            content={content}
            weight={weight}
            candidateBatchCandidateCount={candidateBatchCandidatesCount}
            requirementMetCandidatesCount={requirementMetCandidatesCount}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
          <RequirementRecommendationView
            recommendationsLoading={recommendationLoading}
            adjustedRequirementLoading={adjustedRequirementLoading}
            isRecommendationDismissed={isRecommendationDismissed}
            recommendationCategory={recommendationCategory}
            recommendationText={recommendationText}
            recommendationCompliant={isRecommendationCompliant}
            requirementContent={content}
            originalRequirementContent={originalRequirementContent}
            adjustedRequirementExplanationText={
              adjustedRequirementExplanationText
            }
            onDismiss={handleDismissRecommendation}
            onFix={handleFixRecommendation}
            onDelete={handleDelete}
            onUndo={handleUndo}
          />
          <StepSlider
            field={{
              marks,
              step: 0.5,
              containerSx: sliderContainer,
              disabled: isMandatory
            }}
            value={weight}
            onChange={(value) => {
              setFieldValue(weightField, value);
            }}
          />
          <Checkbox
            field={{
              checkboxLabel: "Knockout if requirement isn't met",
              containerSx: {
                m: 0,
                ml: 0.2, // align checkbox
                fontWeight: 'bold',
                fontSize: '20px'
              }
            }}
            value={isMandatory}
            onChange={(value) => {
              setFieldValue(weightField, value ? 2 : weight);
              setFieldValue(mandatoryField, value);
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};
